import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { AdmanPageContextPayload } from '../../types/adman';
import { ADMAN_DEFAULT_ROUTE_INFO, AdmanRouteInfo, resolveAdmanRouteInfo } from './admanRouteMap';

// ページ側から任意の追加文脈（表示中の商品IDなど）を渡すためのオプトインAPI。
//
// authenticatedContent（App.tsx L260-271）への追加は1行のみという制約があり、
// children を Provider で包むことができない。そのため React Context ではなく
// モジュールスコープの軽量な pub/sub で実装し、Provider 無しでも動くようにしている。
//
// 重要: この extra/context は**フロントエンド内部の受け渡し用**であり、そのまま
// サーバーへ送信されることはない。AdmanPanel が必要なキー（例: product_ids）だけを
// 取り出して AdmanMessageRequest.page の明示的なキーとして送る。任意の塊を検証なしに
// バックエンドへ素通しすると、ページ側が入れた想定外のデータがAIに渡る経路になり得る
// （バックエンドの page はキー数上限4・検証ルールの無いキーは弾かれる設計）。
// サーバーへ渡したい情報が増えたら、その都度バックエンドに検証ルールを追加すること。
type ExtraContextListener = (extra: Record<string, unknown> | null) => void;

let currentExtraContext: Record<string, unknown> | null = null;
const extraContextListeners = new Set<ExtraContextListener>();

const notifyExtraContextListeners = (): void => {
  extraContextListeners.forEach((listener) => listener(currentExtraContext));
};

export const useSetAdmanPageContext = (extra: Record<string, unknown> | null | undefined): void => {
  const serialized = JSON.stringify(extra ?? null);

  useEffect(() => {
    const value = extra ?? null;
    currentExtraContext = value;
    notifyExtraContextListeners();
    return () => {
      // 離脱時は自分がセットした文脈だけをクリアする（後からセットされた別ページの
      // 文脈を誤って消さないため）。
      if (currentExtraContext === value) {
        currentExtraContext = null;
        notifyExtraContextListeners();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serialized]);
};

export interface AdmanPageContext extends AdmanPageContextPayload {
  routeInfo: AdmanRouteInfo;
  // useSetAdmanPageContext で渡された生の追加文脈。ワイヤー契約（AdmanPageContextPayload）
  // には含まれない＝AdmanPanelはこれをそのまま送らず、必要なキーだけ取り出して使う。
  context?: Record<string, unknown>;
}

export const useAdmanPageContext = (): AdmanPageContext => {
  const location = useLocation();
  const [extra, setExtra] = useState<Record<string, unknown> | null>(currentExtraContext);

  useEffect(() => {
    const listener: ExtraContextListener = (value) => setExtra(value);
    extraContextListeners.add(listener);
    return () => {
      extraContextListeners.delete(listener);
    };
  }, []);

  // 未知のパスでも落ちない（resolveAdmanRouteInfo が汎用の説明にフォールバックする）
  const routeInfo = resolveAdmanRouteInfo(location.pathname);

  return {
    path: location.pathname,
    title: routeInfo.title,
    context: extra ?? undefined,
    routeInfo,
  };
};

export { ADMAN_DEFAULT_ROUTE_INFO };

// テスト用: モジュールスコープの状態をリセットする。
export const __resetAdmanPageExtraContextForTests = (): void => {
  currentExtraContext = null;
  extraContextListeners.clear();
};
